import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target } from "lucide-react";

/**
 * OptionsEditor — isolated state textarea + per-option scoring panel.
 *
 * Why isolated state?
 *  - Parsing options on every keystroke causes the cursor to jump and breaks
 *    multi-word entries like "Contemporary Drama" by re-creating the array on
 *    every char typed.
 *  - We keep `rawText` local; only commit (parse → array of {label, score})
 *    on blur. Scores from existing options are preserved on re-commit.
 *
 * Data shape (preferred):  [{ label: string, score: number|null }]
 * Backward-compatible:     [string, ...]
 *
 * Props:
 *  - field: the parent field object (must include `options` and optionally `include_in_score`)
 *  - index: index of the field for testid + parent updates
 *  - updateField: (index, patchObj) => void
 *  - showScoringPanel?: boolean — defaults to `field.include_in_score === true`
 *  - testIdPrefix?: string — prefix for option-* test ids (defaults to "field")
 */
export default function OptionsEditor({
  field,
  index,
  updateField,
  showScoringPanel,
  testIdPrefix = "field",
}) {
  const getLabel = (o) => (typeof o === "object" ? o?.label || "" : String(o ?? ""));

  const optionsToText = (opts) => (opts || []).map(getLabel).join("\n");

  const [rawText, setRawText] = useState(() => optionsToText(field.options));
  const [isFocused, setIsFocused] = useState(false);

  // Re-sync from parent when options change externally (e.g. AI generation, regenerate),
  // but never while user is actively typing.
  useEffect(() => {
    if (!isFocused) {
      setRawText(optionsToText(field.options));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.options, isFocused]);

  // Parse rawText -> structured options, preserving existing scores by label.
  const parseToOptions = (text, oldOptions) => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    const scoreByLabel = new Map();
    (oldOptions || []).forEach((o) => {
      if (typeof o === "object" && o?.label) scoreByLabel.set(o.label.trim(), o.score ?? null);
    });
    return lines.map((label) => ({
      label,
      score: scoreByLabel.has(label) ? scoreByLabel.get(label) : null,
    }));
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setRawText(text);
    // Commit to parent on every change so include_in_score panel sees fresh options.
    // Cursor doesn't jump because the <Textarea> binds to local `rawText`, not `field.options`.
    updateField(index, { options: parseToOptions(text, field.options) });
  };

  // Scoring panel reads from committed parent state (NOT live textarea text)
  const committedOptions = (field.options || [])
    .map((o) => ({
      label: getLabel(o).trim(),
      score: typeof o === "object" ? (o?.score ?? null) : null,
    }))
    .filter((o) => o.label);

  const showPanel =
    typeof showScoringPanel === "boolean" ? showScoringPanel : field.include_in_score === true;

  const handleScoreChange = (optIdx, value) => {
    const val =
      value === ""
        ? null
        : Math.min(100, Math.max(0, parseInt(value, 10) || 0));
    const updated = (field.options || []).map((o, i) => {
      const label = getLabel(o);
      if (i === optIdx) return { label, score: val };
      return typeof o === "object" ? o : { label, score: null };
    });
    updateField(index, { options: updated });
  };

  return (
    <div className="space-y-2">
      <Label>Options (one per line)</Label>
      <Textarea
        value={rawText}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={"Option 1\nOption 2\nOption 3"}
        className="min-h-[100px]"
        data-testid={`${testIdPrefix}-options-${index}`}
      />

      {showPanel && committedOptions.length > 0 && (
        <div
          className="p-3 bg-indigo-50/50 rounded-lg border border-indigo-100 space-y-1.5"
          data-testid={`option-scores-panel-${index}`}
        >
          <p className="text-xs font-medium text-indigo-600 flex items-center gap-1">
            <Target className="w-3 h-3" /> Option Scores
            <span className="text-zinc-400 font-normal">
              (optional — helps AI score deterministically)
            </span>
          </p>
          {committedOptions.map((opt, optIdx) => (
            <div
              key={`${opt.label}-${optIdx}`}
              className="flex items-center gap-2"
            >
              <span className="text-sm text-zinc-700 flex-1 truncate">{opt.label}</span>
              <Input
                type="number"
                min="0"
                max="100"
                placeholder="—"
                value={opt.score ?? ""}
                onChange={(e) => handleScoreChange(optIdx, e.target.value)}
                className="w-16 h-7 text-xs text-center"
                data-testid={`option-score-${index}-${optIdx}`}
              />
            </div>
          ))}
          <p className="text-[10px] text-zinc-400">
            0-100 per option. Leave blank for equal weighting.
          </p>
        </div>
      )}
    </div>
  );
}
